"use client";

interface EmbeddedEventMapProps {
  latitude: number;
  longitude: number;
  venueName?: string | null;
  className?: string;
  /** Lime overlay badge label ("Cómo llegar"). Also used as the fallback row label. */
  directionsLabel?: string;
  /** Accessible title for the iframe. */
  mapTitle?: string;
}

/**
 * Builds the Google Maps deep link used by the "Cómo llegar" badge and the
 * no-key fallback. Same URL shape as the mobile app.
 */
export function buildVenueMapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

/**
 * Compact venue map for the event detail page.
 *
 * With `NEXT_PUBLIC_GOOGLE_MAPS_EMBED_API_KEY` it renders the free Google Maps
 * Embed iframe inside a 16:9 rounded frame (max ~260px tall) with a lime
 * "Cómo llegar" badge that opens Google Maps. Without the key it renders a
 * compact on-brand row linking to Google Maps instead of an empty rectangle —
 * the same fallback the mobile `VenueMapPreview` uses.
 */
export function EmbeddedEventMap({
  latitude,
  longitude,
  venueName,
  className = "",
  directionsLabel = "Cómo llegar",
  mapTitle = "Mapa del evento",
}: EmbeddedEventMapProps) {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_API_KEY || "";
  const mapsUrl = buildVenueMapsUrl(latitude, longitude);

  if (!apiKey) {
    return (
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`gruv-map__fallback ${className}`.trim()}
        data-testid="venue-map-fallback"
      >
        <span className="gruv-map__fallback-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="m3 11 18-8-8 18-2-8-8-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="gruv-map__fallback-text">{directionsLabel}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    );
  }

  const embedUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${latitude},${longitude}`;

  return (
    <div className={`gruv-map ${className}`.trim()} data-testid="venue-map">
      <iframe
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={embedUrl}
        title={venueName ? `${mapTitle}: ${venueName}` : mapTitle}
        className="gruv-map__frame"
      />
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="gruv-map__badge"
        aria-label={directionsLabel}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="m3 11 18-8-8 18-2-8-8-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        <span>{directionsLabel}</span>
      </a>
    </div>
  );
}
